import {
  computed,
  inject,
  Injectable,
  linkedSignal,
  resource,
  signal,
} from '@angular/core';
import { UserInfoService } from '@softline/auth';
import { debouncedSignal } from '@softline/core';
import { GarageOrder } from '../models/garage-order';
import { OrderItem } from '../models/order-item';
import { OrderNote } from '../models/order-note';
import { mapOrderPage, OrderPage } from '../models/order-page';
import { OrderTypeEnum } from '../models/order-type-enum';
import { TimeReport } from '../models/time-report';
import { GarageOrderItemRepository } from '../repositories/garage-order-item.repository';
import {
  GarageOrderCloseMethod,
  GarageOrderRepository,
} from '../repositories/garage-order.repository';
import { OrderNotesRepository } from '../repositories/order-notes.repository';
import { ProductOrderItemRepository } from '../repositories/product-order-item.repository';
import { ProductOrderRepository } from '../repositories/product-order.repository';
import { TimeReportRepository } from '../repositories/time-report.repsitory';

interface OrderListParams {
  limit: number | undefined;
  offset: number | undefined;
  query: string | undefined;
  onlyOwn: boolean;
}

/** 'none' keeps both lists idle until a page that renders them declares its type. */
export type OrderListType = OrderTypeEnum | 'all' | 'none';

@Injectable()
export class OrderService {
  private readonly garageOrderRepository = inject(GarageOrderRepository);
  private readonly productOrderRepository = inject(ProductOrderRepository);
  private readonly timeReportRepository = inject(TimeReportRepository);
  private readonly garageOrderItemRepository = inject(GarageOrderItemRepository);
  private readonly productOrderItemRepository = inject(ProductOrderItemRepository);
  private readonly orderNotesRepository = inject(OrderNotesRepository);

  private readonly userInfo = inject(UserInfoService);
  public readonly lstfixId = computed(() => this.userInfo.value()?.employeeId);

  //* PRIVATE API *//
  public readonly garageOrderSearchString = signal<string | undefined>(undefined);
  public readonly productOrderSearchString = signal<string | undefined>(undefined);

  private readonly debouncedGarageQuery = debouncedSignal(
    this.garageOrderSearchString,
    300
  );
  private readonly debouncedProductQuery = debouncedSignal(
    this.productOrderSearchString,
    300
  );

  #garageLoadVersion = 0;
  #productLoadVersion = 0;

  public readonly onlyOwnOrders = signal<boolean>(false);

  public readonly offset = signal<number>(0);

  /** Restricts list loading to the type a page actually renders; 'all' loads both. */
  public readonly listType = signal<OrderListType>('all');

  private static readonly paramsEqual = (
    a: OrderListParams | undefined,
    b: OrderListParams | undefined
  ): boolean =>
    a === b ||
    (!!a &&
      !!b &&
      a.limit === b.limit &&
      a.offset === b.offset &&
      a.query === b.query &&
      a.onlyOwn === b.onlyOwn);

  private readonly garageOrdersParams = computed<OrderListParams | undefined>(
    () =>
      this.listType() === OrderTypeEnum.PRODUCT || this.listType() === 'none'
        ? undefined
        : {
            limit: this.limit(),
            offset: this.offset(),
            query: this.debouncedGarageQuery(),
            onlyOwn: this.onlyOwnOrders(),
          },
    { equal: OrderService.paramsEqual }
  );
  private readonly productOrdersParams = computed<OrderListParams | undefined>(
    () =>
      this.listType() === OrderTypeEnum.GARAGE || this.listType() === 'none'
        ? undefined
        : {
            limit: this.limit(),
            offset: this.offset(),
            query: this.debouncedProductQuery(),
            onlyOwn: this.onlyOwnOrders(),
          },
    { equal: OrderService.paramsEqual }
  );

  readonly #garagePageCache = new Map<string, OrderPage>();
  readonly #productPageCache = new Map<string, OrderPage>();

  private static readonly pageCacheKey = (p: OrderListParams): string =>
    `${p.query ?? ''}|${p.onlyOwn}|${p.limit}|${p.offset}`;

  public readonly garageOrdersResource = resource({
    params: this.garageOrdersParams,
    loader: async ({ params }) => {
      const key = OrderService.pageCacheKey(params);
      const cached = this.#garagePageCache.get(key);
      if (cached) {
        this.#garageOrders.set(cached.items);
        this.#totalGarageOrders.set(cached.totalCount);
        return;
      }
      const version = ++this.#garageLoadVersion;
      const page = mapOrderPage(
        await this.garageOrderRepository.retrieve(
          params.query,
          params.limit,
          params.offset,
          params.onlyOwn
        )
      );
      if (version !== this.#garageLoadVersion) return;
      this.#garagePageCache.set(key, page);
      this.#garageOrders.set(page.items);
      this.#totalGarageOrders.set(page.totalCount);
    },
  });

  private readonly productOrdersResource = resource({
    params: this.productOrdersParams,
    loader: async ({ params }) => {
      const key = OrderService.pageCacheKey(params);
      const cached = this.#productPageCache.get(key);
      if (cached) {
        this.#productOrders.set(cached.items);
        this.#totalProductOrders.set(cached.totalCount);
        return;
      }
      const version = ++this.#productLoadVersion;
      const page = mapOrderPage(
        await this.productOrderRepository.retrieve(
          params.query,
          params.limit,
          params.offset,
          params.onlyOwn
        )
      );
      if (version !== this.#productLoadVersion) return;
      this.#productPageCache.set(key, page);
      this.#productOrders.set(page.items);
      this.#totalProductOrders.set(page.totalCount);
    },
  });

  #invalidateOrderPageCache(): void {
    this.#garagePageCache.clear();
    this.#productPageCache.clear();
  }

  private readonly garageOrderResource = resource({
    params: () => this.#garageOrderId(),
    loader: async ({ params }) => {
      const orderId = params;
      if (!orderId) {
        return null;
      }
      const request = await this.garageOrderRepository.retrieveById(orderId);
      this.#garageOrder.set(request);
      return request;
    },
  });

  private readonly productOrderResource = resource({
    params: () => this.#productOrderId(),
    loader: async ({ params }) => {
      const orderId = params;
      if (!orderId) {
        return null;
      }
      const request = await this.productOrderRepository.retrieveById(orderId);
      this.#productOrder.set(request);
      return request;
    },
  });

  public readonly garageOrderTimeReportsResource = resource({
    params: () => this.#garageOrderId(),
    loader: async ({ params }) => {
      const orderId = params;
      if (!orderId) {
        return null;
      }
      const request = await this.timeReportRepository.getTimeReportsByOrderId(orderId);
      this.#garageOrderTimeReports.set(request);
      return request;
    },
  });

  private readonly garageSignatureImageResource = resource({
    params: () => this.#garageOrder()?.signature ?? null,
    loader: async ({ params }) => {
      if (!params) {
        return null;
      }
      return this.garageOrderRepository.getSignatureImage(params);
    },
  });

  async loadGarageOrderItems(orderId: number | null): Promise<void> {
    if (!orderId) {
      this.#garageOrderItems.set([]);
      return;
    }
    this.#garageOrderItems.set(
      (await this.garageOrderItemRepository.retrieve(orderId)) ?? []
    );
  }

  async loadProductOrderItems(orderId: number | null): Promise<void> {
    if (!orderId) {
      this.#productOrderItems.set([]);
      return;
    }
    this.#productOrderItems.set(
      (await this.productOrderItemRepository.retrieve(orderId)) ?? []
    );
  }

  readonly garageOrderNotesResource = resource({
    params: () => this.#garageOrderId(),
    loader: async ({ params }) => {
      const orderId = params;
      if (!orderId) {
        return null;
      }
      const request = await this.orderNotesRepository.retrieve(orderId);
      this.#garageOrderNotes.set(request);
      return request;
    },
  });

  readonly #garageOrders = signal<GarageOrder[]>([]);
  readonly #productOrders = signal<GarageOrder[]>([]);
  readonly #totalGarageOrders = signal<number>(0);
  readonly #totalProductOrders = signal<number>(0);
  readonly #garageOrder = signal<GarageOrder | null>(null);
  readonly #productOrder = signal<GarageOrder | null>(null);
  readonly #garageOrderId = signal<number | null>(null);
  readonly #productOrderId = signal<number | null>(null);
  readonly #garageOrderTimeReports = signal<TimeReport[]>([]);
  readonly #garageOrderItems = signal<OrderItem[]>([]);
  readonly #garageOrderNotes = signal<OrderNote[]>([]);
  readonly #productOrderItems = signal<OrderItem[]>([]);

  //* PUBLIC API *//

  public readonly limit = signal<number | undefined>(25);
  public readonly withScheduledOrders = signal<boolean>(false);

  public readonly garageOrder = linkedSignal(() => this.#garageOrder());
  public readonly garageOrderLoading = computed(() =>
    this.garageOrderResource.isLoading()
  );
  public readonly garageSignatureImage = computed<Blob | null>(
    () => this.garageSignatureImageResource.value() ?? null
  );
  public readonly garageSignatureImageLoading = computed(() =>
    this.garageSignatureImageResource.isLoading()
  );
  public readonly garageOrdersError = this.garageOrdersResource.error;

  public readonly productOrder = linkedSignal(() => this.#productOrder());
  public readonly productOrderLoading = computed(() =>
    this.productOrderResource.isLoading()
  );

  public readonly garageOrders = linkedSignal(() => this.#garageOrders());
  public readonly garageOrdersLoading = computed(() =>
    this.garageOrdersResource.isLoading()
  );
  public readonly totalGarageOrders = computed(() => this.#totalGarageOrders());
  public readonly productOrders = linkedSignal(() => this.#productOrders());
  public readonly productOrdersLoading = computed(() =>
    this.productOrdersResource.isLoading()
  );
  public readonly productOrdersError = this.productOrdersResource.error;
  public readonly totalProductOrders = computed(() => this.#totalProductOrders());

  reloadProductOrders(): void {
    this.#invalidateOrderPageCache();
    this.productOrdersResource.reload();
  }

  public readonly garageOrderTimeReports = linkedSignal(() =>
    this.#garageOrderTimeReports()
  );
  public readonly orderTimeReportsLoading = computed(() =>
    this.garageOrderTimeReportsResource.isLoading()
  );

  public readonly garageOrderNotes = linkedSignal(() => this.#garageOrderNotes());
  public readonly garageOrderNotesLoading = computed(() =>
    this.garageOrderNotesResource.isLoading()
  );

  public readonly garageOrderItems = linkedSignal(() => this.#garageOrderItems());
  public readonly productOrderItems = linkedSignal(() => this.#productOrderItems());

  //* FILTERED ORDERS *//

  readonly filteredGarageOrders = computed(() => {
    let orders = this.garageOrders();
    if (!this.withScheduledOrders()) {
      orders = orders.filter((order) => order.scheduled && order.scheduled.length == 0);
    }
    return orders;
  });

  public readonly filteredProductOrders = computed(() => {
    let orders = this.productOrders();
    if (!this.withScheduledOrders()) {
      orders = orders.filter((order) => order.scheduled && order.scheduled.length == 0);
    }
    return orders;
  });

  //* Create Update Delete *//
  public async createOrder(order: GarageOrder): Promise<GarageOrder> {
    let response: GarageOrder;
    if (order.type === OrderTypeEnum.GARAGE) {
      response = await this.garageOrderRepository.create(order);
      if (response) {
        this.#garageOrders.set([response, ...this.#garageOrders()]);
        this.#totalGarageOrders.update((t) => t + 1);
        this.#garageOrder.set(response);
      }
    } else {
      response = await this.productOrderRepository.create(order);
      if (response) {
        this.#productOrders.set([response, ...this.#productOrders()]);
        this.#totalProductOrders.update((t) => t + 1);
        this.#productOrder.set(response);
      }
    }
    this.#invalidateOrderPageCache();
    return response;
  }

  public async updateOrderById(
    orderId: number,
    order: GarageOrder
  ): Promise<GarageOrder> {
    let updatedOrder: GarageOrder;
    if (order.type === OrderTypeEnum.GARAGE) {
      updatedOrder = await this.garageOrderRepository.update(orderId, order);
      if (updatedOrder) {
        this.#garageOrders.set(
          this.#garageOrders().map((o) => (o.id === orderId ? updatedOrder : o))
        );
      }
      this.#garageOrder.set(updatedOrder);
    } else {
      updatedOrder = await this.productOrderRepository.update(orderId, order);
      if (updatedOrder) {
        this.#productOrders.set(
          this.#productOrders().map((o) => (o.id === orderId ? updatedOrder : o))
        );
      }
      this.#productOrder.set(updatedOrder);
    }
    this.#invalidateOrderPageCache();
    return updatedOrder;
  }

  public async closeOrder(
    orderId: number,
    method: GarageOrderCloseMethod = 'approval'
  ): Promise<GarageOrder> {
    const closed = await this.garageOrderRepository.close(orderId, method);
    if (closed) {
      this.#garageOrder.set(closed);
      this.#garageOrders.set(
        this.#garageOrders().map((o) => (o.id === orderId ? closed : o))
      );
    }
    this.#invalidateOrderPageCache();
    return closed;
  }

  /* TODO: Implement deleteOrderById in backend and uncomment this method
  public deleteOrderById(orderId: number): void {
    return this.garageOrderRepository.delete(orderId).then(() => {
      this.#garageOrders.set(this.#garageOrders().filter((o) => o.id !== orderId));
      this.#totalGarageOrders.update((t) => t - 1);
      if (this.#garageOrder()?.id === orderId) {
        this.#garageOrder.set(null);
      }
      this.#invalidateOrderPageCache();
    });
  }
  */

  //* LOADER METHODS *//
  public loadGarageOrderById(orderId: number | null): void {
    this.#garageOrderId.set(orderId);
    void this.loadGarageOrderItems(orderId);
  }
  public loadProductOrderById(orderId: number | null): void {
    this.#productOrderId.set(orderId);
    void this.loadProductOrderItems(orderId);
  }

  public getGarageOrderById(orderId: number): Promise<GarageOrder> {
    return this.garageOrderRepository.retrieveById(orderId);
  }
  public getProductOrderById(orderId: number): Promise<GarageOrder> {
    return this.productOrderRepository.retrieveById(orderId);
  }

  public async loadDefaultGarageOrder(): Promise<GarageOrder> {
    return this.garageOrderRepository.retrieveDefault();
  }

  //* HELPER METHODS *//
  public setOrderScheduled(orderId: number): void {
    const productOrders = [...this.#productOrders()];
    const garageOrders = [...this.#garageOrders()];
    productOrders.find((order) => order.id === orderId)?.scheduled?.push({ start: '' });
    this.#productOrders.set(productOrders);
    garageOrders.find((order) => order.id === orderId)?.scheduled?.push({ start: '' });
    this.#garageOrders.set(garageOrders);
  }

  public removeOrderScheduled(orderId: number): void {
    const productOrders = [...this.#productOrders()];
    const garageOrders = [...this.#garageOrders()];
    const garageOrder = garageOrders.find((order) => order.id === orderId);
    if (garageOrder) {
      garageOrder.scheduled = [];
    }
    this.#productOrders.set(productOrders);
    const productOrder = productOrders.find((order) => order.id === orderId);
    if (productOrder) {
      productOrder.scheduled = [];
    }
    this.#garageOrders.set(garageOrders);
  }

  public setWithScheduledOrders(value: boolean): void {
    this.withScheduledOrders.set(value);
  }

  public async updateOrderDescription(
    orderId: number,
    description: string
  ): Promise<void> {
    await this.updateOrderById(orderId, {
      ...this.#garageOrder(),
      description: description,
    } as GarageOrder);
  }

  public async createOrderNote(orderId: number, note: OrderNote): Promise<OrderNote> {
    const response = await this.orderNotesRepository.create(orderId, note);
    if (response) {
      this.#garageOrderNotes.set([...this.#garageOrderNotes(), response]);
    }
    return response;
  }

  public async createOrderTimeReport(
    orderId: number,
    report: Partial<TimeReport>
  ): Promise<TimeReport> {
    const response = await this.timeReportRepository.createTimeReport({
      orderId,
      date: report.date,
      from: report.from ?? undefined,
      to: report.to ?? undefined,
      description: report.description,
      activity: report.activity ? { id: report.activity.id } : undefined,
    });
    if (response) {
      this.#garageOrderTimeReports.set([...this.#garageOrderTimeReports(), response]);
    }
    return response;
  }

  public async updateOrderTimeReport(
    reportId: number,
    report: Partial<TimeReport>
  ): Promise<TimeReport> {
    const response = await this.timeReportRepository.updateTimeReport(reportId, {
      orderId: report.orderId,
      date: report.date,
      from: report.from ?? undefined,
      to: report.to ?? undefined,
      description: report.description,
      activity: report.activity ? { id: report.activity.id } : undefined,
    });
    this.garageOrderTimeReportsResource.reload();
    return response;
  }

  public async deleteOrderTimeReport(report: TimeReport): Promise<void> {
    await this.timeReportRepository.deleteTimeReportById(report.id);
    this.#garageOrderTimeReports.set(
      this.#garageOrderTimeReports().filter((r) => r.id !== report.id)
    );
  }

  public async getGarageOrderItemTemplate(
    orderItem: Partial<OrderItem>
  ): Promise<OrderItem> {
    const response = await this.garageOrderItemRepository.retrieveTemplate(orderItem);
    return response;
  }

  public async getProductOrderItemTemplate(
    orderItem: Partial<OrderItem>
  ): Promise<OrderItem> {
    const response = await this.productOrderItemRepository.retrieveTemplate(orderItem);
    return response;
  }

  public async createGarageOrderItem(item: Partial<OrderItem[]>): Promise<OrderItem[]> {
    const response = await this.garageOrderItemRepository.create(item);
    if (response) {
      this.#garageOrderItems.set(this.mergeItems(this.#garageOrderItems(), response));
    }
    return response;
  }

  public async createProductOrderItem(item: Partial<OrderItem[]>): Promise<OrderItem[]> {
    const response = await this.productOrderItemRepository.create(item);
    if (response) {
      this.#productOrderItems.set(this.mergeItems(this.#productOrderItems(), response));
    }
    return response;
  }

  private mergeItems(existing: OrderItem[], incoming: OrderItem[]): OrderItem[] {
    const incomingIds = new Set(incoming.map((i) => i.id));
    return [...existing.filter((i) => !incomingIds.has(i.id)), ...incoming];
  }

  public async updateGarageOrderItem(
    itemId: number,
    item: Partial<OrderItem>
  ): Promise<OrderItem> {
    const response = await this.garageOrderItemRepository.update(itemId, item);
    if (response) {
      this.#garageOrderItems.set(
        this.#garageOrderItems().map((i) => (i.id === itemId ? response : i))
      );
    }
    return response;
  }

  public async updateProductOrderItem(
    itemId: number,
    item: Partial<OrderItem>
  ): Promise<OrderItem> {
    const response = await this.productOrderItemRepository.update(itemId, item);
    if (response) {
      this.#productOrderItems.set(
        this.#productOrderItems().map((i) => (i.id === itemId ? response : i))
      );
    }
    return response;
  }

  public async deleteGarageOrderItem(item: OrderItem): Promise<void> {
    await this.garageOrderItemRepository.delete(item.id);
    this.#garageOrderItems.set(this.#garageOrderItems().filter((i) => i.id !== item.id));
  }

  public async deleteProductOrderItem(item: OrderItem): Promise<void> {
    await this.productOrderItemRepository.delete(item.id);
    this.#productOrderItems.set(
      this.#productOrderItems().filter((i) => i.id !== item.id)
    );
  }

  reloadAllOrders(): void {
    this.#invalidateOrderPageCache();
    this.garageOrdersResource.reload();
    this.productOrdersResource.reload();
  }

  reloadGarageOrders(): void {
    this.#invalidateOrderPageCache();
    this.garageOrdersResource.reload();
  }

  async signOrder(orderId: number, signature: Blob): Promise<GarageOrder> {
    const signed = await this.garageOrderRepository.signOrder(orderId, signature);
    if (signed) {
      this.#garageOrder.update((o) =>
        o && o.id === orderId ? { ...o, signature: signed.signature } : o
      );
      this.#productOrder.update((o) =>
        o && o.id === orderId ? { ...o, signature: signed.signature } : o
      );
    }
    return signed;
  }
}
